import React, { Fragment, useRef, useEffect } from 'react';
import { useParams } from 'react-router';

import { Container, Empty, Title, Description, EmergencyContact } from './MessageList.styled';
import { Headset } from 'assets/icons/Icons';
import { Loader } from 'components/Loader/Loader';
import useContactInfo from 'hooks/useContactInfo';
import { Category, MessageParams } from 'screens/Messages/Messages.type';
import { getCategoryInfo, isResolvedConversation } from 'screens/Messages/Messages.utils';

import { Props } from './MessageList.types';
import { ResolvedConversationInfo } from './ResolvedConversationInfo';
import { CATEGORY_SLUG } from '../../Messages.constants';
import { CategoryIcon } from '../CategoryIcon/CategoryIcon';
import { DateSeparator } from '../DateSeparator/DateSeparator';
import { Message } from '../Message/Message';
import { MessageOwner } from '../MessageOwner/MessageOwner';
import { ReassignmentInfo } from '../ReassignmentInfo/ReassignmentInfo';
export { CATEGORY_SLUG } from '../../Messages.constants';

export const MessageList = ({ loading, conversation, onSend }: Props): React.JSX.Element => {
  const { categoryName } = useParams<MessageParams>();
  const messageRef = useRef<HTMLDivElement>(null);
  const scrolledConversationIdRef = useRef<string | undefined>(undefined);
  const prevMessageCountRef = useRef<number>(0);
  const { number } = useContactInfo();

  const currentCategory = getCategoryInfo(categoryName ?? '');

  const isConversationResolved = isResolvedConversation(conversation?.status);
  const conversationId = conversation?.id;
  const numberOfMessages = conversation?.messages?.length ?? 0;

  useEffect(() => {
    if (!messageRef.current || loading || !conversationId) return;

    const isNewConversation = scrolledConversationIdRef.current !== conversationId;
    const hasNewMessages = numberOfMessages > prevMessageCountRef.current;

    if (isNewConversation || hasNewMessages) {
      messageRef.current.scrollTop = messageRef.current.scrollHeight;
      scrolledConversationIdRef.current = conversationId;
      prevMessageCountRef.current = numberOfMessages;
    }
  }, [loading, conversationId, numberOfMessages]);

  if (loading) return <Loader container />;

  return (
    <Container ref={messageRef}>
      <Empty>
        <CategoryIcon size="lg" icon={currentCategory.icon} />
        <Title>{currentCategory?.title}</Title>
        <Description tag="h5">{currentCategory.description}</Description>
        {!isConversationResolved && (
          <EmergencyContact>
            <Headset type="solid" />
            <span>{getEmergencyMessage(currentCategory, number)}</span>
          </EmergencyContact>
        )}
      </Empty>

      {conversation?.messages?.map((message, index) => {
        const previousMessage = conversation.messages[index - 1];

        return (
          <Fragment key={index}>
            <DateSeparator previousMessage={previousMessage} currentMessage={message} />

            <ReassignmentInfo message={message} conversation={conversation} />

            <MessageOwner
              previousMessage={previousMessage}
              currentMessage={message}
              currentCategory={currentCategory}
            />

            <Message
              message={message}
              conversationId={conversation.id}
              onSend={onSend}
              latestMessage={index === conversation?.messages?.length - 1}
            />
          </Fragment>
        );
      })}

      <ResolvedConversationInfo conversation={conversation} />
    </Container>
  );
};

const isBenefit = (slug: string) =>
  slug === CATEGORY_SLUG.BENEFITS || slug === CATEGORY_SLUG.EMPLOYER_BENEFIT;

const getEmergencyMessage = (currentCategory: Category, defaultNumber: string) => {
  if (currentCategory.slug === CATEGORY_SLUG.BILLING)
    return 'For immediate assistance related to your bill, please call the phone number on your statement. If this is a medical emergency, please call 911.';

  return (
    <>
      {currentCategory.slug === 'medical' ? (
        <span>
          If this is a medical/life-threatening emergency, please call 911. Many of the answers you
          need are easily found here:{' '}
          <a href="https://pf2-public-patient-portal-resources.s3.us-east-2.amazonaws.com/Portal+Message+FAQ.pdf">
            FAQ
          </a>
          . If you do send a message, we typically reply within 2 business days. If this is an
          urgent medical issue that cannot wait until the next day, call the after-hours on-call
          team at <a href={`tel:${defaultNumber}`}>{defaultNumber}</a>, option 5.
        </span>
      ) : (
        `We typically reply within ${
          isBenefit(currentCategory.slug) ? '3-4' : '2'
        } business days. If
      this is a medical emergency, please call 911.`
      )}
    </>
  );
};
